import axios from "axios";
import { buildApiUrl, isSupabaseFunctionsApi } from "../config/api";
import { chunkArray } from "./bulkStudentUpload";

/** Smaller chunks on Supabase to avoid edge function CPU/time limits. */
const JSON_CHUNK_SIZE = isSupabaseFunctionsApi() ? 3 : 100;

function callerParams(schoolId) {
    return { schoolId, adminID: schoolId };
}

export async function validateBulkStudentRows(schoolId, rows) {
    const { data } = await axios.post(buildApiUrl("/StudentBulkImport/validate"), {
        ...callerParams(schoolId),
        rows,
    });
    return data;
}

export async function previewBulkStudentFile(schoolId, file, columnMapping = null) {
    const form = new FormData();
    form.append("file", file);
    form.append("schoolId", schoolId);
    if (columnMapping) {
        form.append("columnMapping", JSON.stringify(columnMapping));
    }
    const { data } = await axios.post(buildApiUrl("/admin/bulk-students/preview"), form, {
        headers: { "Content-Type": "multipart/form-data" },
        params: callerParams(schoolId),
    });
    return data;
}

/** Upload CSV/Excel file to POST /admin/bulk-students (upsert, detailed report). */
export async function importBulkStudentsFromFile(
    schoolId,
    file,
    { mode = "partial", columnMapping = null, notifyAdmin = true, onProgress } = {}
) {
    const form = new FormData();
    form.append("file", file);
    form.append("schoolId", schoolId);
    form.append("mode", mode);
    form.append("notifyAdmin", notifyAdmin ? "true" : "false");
    if (columnMapping) {
        form.append("columnMapping", JSON.stringify(columnMapping));
    }

    const { data } = await axios.post(buildApiUrl("/admin/bulk-students"), form, {
        headers: { "Content-Type": "multipart/form-data" },
        params: callerParams(schoolId),
        onUploadProgress: onProgress
            ? (e) => {
                  if (e.total) onProgress(Math.round((e.loaded / e.total) * 100));
              }
            : undefined,
    });
    return data;
}

/** Chunked JSON import for large mapped datasets (avoids re-parsing file). */
export async function importBulkStudentsChunked(
    schoolId,
    students,
    {
        mode = "partial",
        fileName = "mapped-import.csv",
        notifyAdmin = true,
        onProgress,
        chunkSize = JSON_CHUNK_SIZE,
    } = {}
) {
    const chunks = chunkArray(students, chunkSize);
    let aggregate = {
        totalProcessed: 0,
        successful: 0,
        failed: 0,
        created: 0,
        updated: 0,
        guardianPhonesCaptured: 0,
        failures: [],
        successes: [],
        importLogId: null,
        smsSent: false,
    };

    for (let i = 0; i < chunks.length; i++) {
        const isLast = i === chunks.length - 1;
        const { data } = await axios.post(buildApiUrl("/StudentBulkImport"), {
            ...callerParams(schoolId),
            students: chunks[i],
            mode,
            fileName,
            notifyAdmin: isLast ? notifyAdmin : false,
        });
        aggregate.totalProcessed += data.totalProcessed ?? chunks[i].length;
        aggregate.successful += data.successful ?? data.importedCount ?? 0;
        aggregate.failed += data.failed ?? data.failedCount ?? 0;
        aggregate.created += data.created ?? 0;
        aggregate.updated += data.updated ?? 0;
        aggregate.guardianPhonesCaptured += data.guardianPhonesCaptured ?? 0;
        if (data.failures?.length) aggregate.failures.push(...data.failures);
        if (data.failures?.length === 0 && data.failed?.length) aggregate.failures.push(...data.failed);
        if (data.successes?.length) aggregate.successes.push(...data.successes);
        if (data.imported?.length) aggregate.successes.push(...data.imported);
        if (data.importLogId) aggregate.importLogId = data.importLogId;
        if (data.smsSent) aggregate.smsSent = true;
        if (onProgress) onProgress(Math.round(((i + 1) / chunks.length) * 100));
    }

    aggregate.message = `${aggregate.successful} students processed (${aggregate.created} created, ${aggregate.updated} updated).`;
    return aggregate;
}

export async function getBulkImportHistory(schoolId, limit = 10) {
    const { data } = await axios.get(buildApiUrl("/admin/bulk-students/history"), {
        params: { ...callerParams(schoolId), limit },
    });
    return data;
}

/** Legacy JSON import — kept for backward compatibility. */
export async function importBulkStudents(schoolId, students, options = {}) {
    const { data } = await axios.post(buildApiUrl("/StudentBulkImport"), {
        ...callerParams(schoolId),
        students,
        mode: options.mode || "partial",
        notifyAdmin: options.notifyAdmin !== false,
    });
    return data;
}
